/**
 * <LiveSessionCard> — an upcoming live tutoring session (organism, L0.1).
 *
 * Mirrors the legacy `StudentLiveCardComponent` (parent view): a non-pressable card whose top
 * row is the bold time + stacked status chips + a 3-dot overflow menu, and whose bottom row is the
 * tutor (`<Avatar>` + name) with overlapping subject chips. No date label (the page renders date
 * section headers above each group) and no join button (parent view is informational).
 *
 * The badges and the overflow menu live in `<LiveStatusBadges>` / `<LiveSessionMenu>`, shared with
 * the other session surfaces (home hero, compact row) so labels can't drift apart — one state badge,
 * at most one context badge (`useSessionBadges`), without « Aujourd'hui » : the rail says it. The card
 * emits a single `onMenuAction(action)` — the consumer routes it to the matching domain handler.
 * The card body is NOT pressable; a success accent rail marks a session today; the card dims for
 * absent / payment-blocked states.
 *
 * Composes DS atoms/molecules only — `<Card>`, `<Avatar>`, `<Text>`, `<Row>`, `<Stack>`,
 * `<SubjectBadges>`, `<LiveStatusBadges>`, `<LiveSessionMenu>`.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { useSessionBadges } from "../../hooks/useSessionBadges"
import { Card } from "../../molecules/Card"
import { LiveSessionMenu, type LiveMenuAction } from "../../molecules/LiveSessionMenu"
import { LiveStatusBadges } from "../../molecules/LiveStatusBadges"
import { SubjectBadges } from "../../molecules/SubjectBadges"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { formatDuration, formatHour, isToday } from "../../../ports/primary/ui/formatters/dateFormatter"

/** Re-exported so the card stays the import site the app already knows. */
export type { LiveMenuAction }

export type LiveSessionCardProps = {
    liveSession: UILiveSession
    /** Emitted when an overflow-menu row is tapped. When omitted, the menu is not rendered. */
    onMenuAction?: (action: LiveMenuAction) => void
    testID?: string
}

export function LiveSessionCard(props: LiveSessionCardProps) {
    const { liveSession, onMenuAction, testID } = props
    const { date, duration, tutor, subjects, statuses, recoveryInfo } = liveSession
    // Derived from the raw ISO date + duration → "10h30 - 1h".
    const timeLabel = `${formatHour(date)} - ${formatDuration(duration)}`
    const today = isToday(date)
    // "Sarah Dupont" → "Sarah D." (first name + last-name initial).
    const lastInitial = tutor.lastname ? `${tutor.lastname.charAt(0)}.` : ""
    const tutorName = [tutor.firstname, lastInitial].filter(Boolean).join(" ")

    const dimmed = statuses.includes("absent") || statuses.includes("payment_blocked")
    const { badges } = useSessionBadges(liveSession, { today: false })

    return (
        <Card
            leftBorderColor={today && !dimmed ? "success" : undefined}
            style={dimmed ? styles.dimmed : undefined}
            testID={testID}
        >
            <Stack gap="2">
                <Row justify="between" align="center">
                    <Row gap="2" align="center" style={styles.shrink}>
                        <Text weight="bold">{timeLabel}</Text>
                        {badges != null && <LiveStatusBadges badges={badges} />}
                    </Row>
                    <LiveSessionMenu statuses={statuses} onAction={onMenuAction} />
                </Row>

                {recoveryInfo != null && (
                    <Text size="text-xs" color="text-muted">
                        {recoveryInfo}
                    </Text>
                )}

                <Row justify="between" align="center" gap="3" style={styles.tutorRow}>
                    <Row gap="2" align="center" style={styles.shrink}>
                        {/* Nœud jaune signature, sans anneau de champion : dans l'espace parent,
                            la couleur du champion est un code que la famille ne connaît pas. */}
                        <Avatar uri={tutor.avatarUrl} name={tutorName} size={28} signed />
                        <Text numberOfLines={1} style={styles.shrink}>
                            {tutorName}
                        </Text>
                        <SubjectBadges subjects={subjects} />
                    </Row>
                </Row>
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    dimmed: {
        backgroundColor: theme.colors["surface-subtle"],
        borderLeftColor: theme.colors.border,
        opacity: 0.7,
    },
    shrink: {
        flexShrink: 1,
    },
    /** Room for the tutor bow-tie that hangs below the avatar (legacy AvatarM). */
    tutorRow: {
        paddingBottom: 6,
    },
}))
